import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isLeader } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CalendarDays, ChevronLeft, ChevronRight, AlertTriangle, Plane, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Weekday labels used to render the header row (Thu/Fri/Sat/...)
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function ymFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function currentYm() {
  return ymFromDate(new Date());
}

function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return ymFromDate(d);
}

function monthDisplayLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

// Small SVG-free color chip cell — matches the attached screenshot's palette.
// `away` = the user clocked in outside their base that day → the nearest
// city's code renders as a tiny amber tag on top of the status letter.
function ScheduleCell({ code, colors, day, testId, away, travel, onClickDay }) {
  const c = colors[code] || { label: code, bg: "#e5e7eb", fg: "#374151" };
  const awayTitle = away
    ? ` · clocked in${away.city_code ? ` near ${away.city_code}` : ""}${away.distance_km != null ? ` — ${away.distance_km >= 10 ? away.distance_km.toFixed(0) : away.distance_km.toFixed(1)} km from base` : ""}`
    : "";
  const travelTitle = travel
    ? ` · TRAVEL: ${travel.note}${travel.set_by_name ? ` (set by ${travel.set_by_name})` : ""}`
    : "";
  return (
    <td
      className={`px-1 py-1 text-center font-mono text-[11px] border border-summit-hairline ${onClickDay ? "cursor-pointer hover:ring-2 hover:ring-inset hover:ring-sky-400" : ""}`}
      style={travel ? { backgroundColor: "#0369a1", color: "#ffffff" } : { backgroundColor: c.bg, color: c.fg }}
      title={`${day} · ${c.label}${awayTitle}${travelTitle}`}
      onClick={onClickDay}
      data-testid={testId}
    >
      {away && (
        <div
          className="leading-none text-[7px] font-bold tracking-tight rounded-sm mx-auto mb-0.5 px-0.5 py-px w-fit"
          style={{ backgroundColor: "#f59e0b", color: "#ffffff" }}
          data-testid={`${testId}-away`}
        >
          {away.city_code || "AWAY"}
        </div>
      )}
      {travel ? (
        <div className="flex flex-col items-center gap-px" data-testid={`${testId}-travel`}>
          <Plane className="w-3.5 h-3.5" />
          <span className="leading-none text-[8px] font-bold tracking-widest">TRVL</span>
        </div>
      ) : (
        code
      )}
    </td>
  );
}

// One employee row — chain leads render bold, members indent with ↳.
function UserRow({ u, indent, isLead, days, colors, onDayClick }) {
  return (
    <tr className="border-b border-summit-hairline hover:bg-summit-glacier/40">
      <td
        className="sticky left-0 bg-white z-10 px-3 py-1.5 border-r border-summit-hairline"
        style={{ minWidth: "220px" }}
      >
        <div className={`text-sm text-summit-ink flex items-center gap-1.5 ${indent ? "pl-5" : ""} ${isLead ? "font-semibold" : ""}`} data-testid={`schedule-user-${u.id}`}>
          {indent && <span className="text-gray-300 font-mono text-[10px]">↳</span>}
          {u.name}
          {u.employment_type === "part_time" && (
            <span className="font-mono text-[9px] uppercase tracking-widest border border-violet-200 bg-violet-50 text-violet-700 rounded px-1 py-0.5" title="Part time — no fixed schedule" data-testid={`schedule-pt-badge-${u.id}`}>PT</span>
          )}
        </div>
        <div className={`font-mono text-[10px] text-gray-400 ${indent ? "pl-5" : ""}`}>
          {u.job_title || ""}
        </div>
      </td>
      {days.map((d) => (
        <ScheduleCell
          key={d}
          code={u.by_day[d]}
          colors={colors}
          day={d}
          away={u.away_by_day?.[d]}
          travel={u.travel_by_day?.[d]}
          onClickDay={onDayClick ? () => onDayClick(u, d) : undefined}
          testId={`schedule-cell-${u.id}-${d}`}
        />
      ))}
    </tr>
  );
}

export default function Schedule() {
  const { user } = useAuth();
  const [view, setView] = useState("month"); // "month" | "pay-period"
  const [monthYm, setMonthYm] = useState(currentYm());
  const [periodNumber, setPeriodNumber] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Folded-away city/location sections.
  const [collapsedCities, setCollapsedCities] = useState(() => new Set());
  // Travel-note dialog — only System Admins & User Admins can open it.
  const canEditTravel = user?.role === "admin" || user?.role === "crew_chief";
  const [travelDlg, setTravelDlg] = useState(null); // {userId, userName, day, note, existing, setByName}
  const [travelSaving, setTravelSaving] = useState(false);

  const openTravelDialog = (u, day) => {
    const existing = u.travel_by_day?.[day];
    setTravelDlg({
      userId: u.id,
      userName: u.name,
      day,
      note: existing?.note || "",
      existing: !!existing,
      setByName: existing?.set_by_name,
    });
  };

  const saveTravel = async () => {
    if (!travelDlg?.note.trim()) {
      toast.error("Enter travel details first.");
      return;
    }
    setTravelSaving(true);
    try {
      await api.put("/schedule/travel", {
        user_id: travelDlg.userId,
        date: travelDlg.day,
        note: travelDlg.note.trim(),
      });
      toast.success(`Travel ${travelDlg.existing ? "updated" : "added"} for ${travelDlg.userName} · ${travelDlg.day}`);
      setTravelDlg(null);
      fetchSchedule();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save travel note");
    } finally {
      setTravelSaving(false);
    }
  };

  const removeTravel = async () => {
    setTravelSaving(true);
    try {
      await api.delete(`/schedule/travel?user_id=${encodeURIComponent(travelDlg.userId)}&date=${encodeURIComponent(travelDlg.day)}`);
      toast.success(`Travel removed for ${travelDlg.userName} · ${travelDlg.day}`);
      setTravelDlg(null);
      fetchSchedule();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove travel note");
    } finally {
      setTravelSaving(false);
    }
  };

  const toggleCity = (code) =>
    setCollapsedCities((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });

  // Initialise current period once so pay-period toggling has a starting anchor.
  useEffect(() => {
    api.get("/pay-periods/current")
      .then((r) => setPeriodNumber(r.data.period_number))
      .catch(() => {});
  }, []);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const anchor = view === "month" ? monthYm : (periodNumber ? String(periodNumber) : "");
      const { data: r } = await api.get(`/schedule?view=${view}${anchor ? `&anchor=${encodeURIComponent(anchor)}` : ""}`);
      setData(r);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Failed to load schedule";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [view, monthYm, periodNumber]);

  useEffect(() => {
    if (view === "pay-period" && !periodNumber) return; // wait for period init
    fetchSchedule();
  }, [view, monthYm, periodNumber, fetchSchedule]);

  // Precompute per-day weekday & day-of-month for the header row.
  const dayHeaders = useMemo(() => {
    if (!data?.days) return [];
    return data.days.map((iso) => {
      const [y, m, d] = iso.split("-").map(Number);
      // Local date is fine — we render weekday & day-of-month strings only.
      const dt = new Date(y, m - 1, d);
      return { iso, weekday: WEEKDAY_LABELS[dt.getDay()], dayOfMonth: d };
    });
  }, [data?.days]);

  // Gate: only leadership tiers can see the Schedule page.
  if (!isLeader(user)) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="schedule-forbidden">
        <div className="flex items-center gap-3 p-4 border border-amber-300 bg-amber-50 rounded-md">
          <AlertTriangle className="w-4 h-4 text-amber-700" />
          <div className="text-sm text-amber-900">
            The Schedule page is available to Supervisors, Managers, Senior Managers, and Admins.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-6 md:py-8">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-gray-500">Schedule · MST</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-summit-ink" data-testid="schedule-title">
            {view === "month" && monthDisplayLabel(monthYm)}
            {view === "pay-period" && data?.anchor && `Pay Period ${data.anchor}`}
          </h1>
          {data && (
            <div className="font-mono text-[11px] text-gray-500 mt-1">
              {data.from_date} → {data.to_date} · {data.days.length} days · {data.locations.length} location{data.locations.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-summit-hairline bg-white overflow-hidden" data-testid="schedule-view-toggle">
            <button
              type="button"
              onClick={() => setView("month")}
              className={`px-3 py-1.5 text-sm ${view === "month" ? "bg-summit-primary text-white" : "text-summit-ink hover:bg-summit-glacier"}`}
              data-testid="schedule-view-month"
            >
              Month
            </button>
            <button
              type="button"
              onClick={() => setView("pay-period")}
              className={`px-3 py-1.5 text-sm border-l border-summit-hairline ${view === "pay-period" ? "bg-summit-primary text-white" : "text-summit-ink hover:bg-summit-glacier"}`}
              data-testid="schedule-view-pay-period"
            >
              Pay Period
            </button>
          </div>
          <div className="inline-flex rounded-md border border-summit-hairline bg-white overflow-hidden">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                if (view === "month") setMonthYm(shiftMonth(monthYm, -1));
                else if (periodNumber) setPeriodNumber(periodNumber - 1);
              }}
              data-testid="schedule-prev"
              className="h-8 px-2"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                if (view === "month") setMonthYm(currentYm());
                else {
                  api.get("/pay-periods/current").then((r) => setPeriodNumber(r.data.period_number)).catch(() => {});
                }
              }}
              data-testid="schedule-today"
              className="h-8 px-3 text-xs border-l border-summit-hairline"
            >
              Today
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                if (view === "month") setMonthYm(shiftMonth(monthYm, +1));
                else if (periodNumber) setPeriodNumber(periodNumber + 1);
              }}
              data-testid="schedule-next"
              className="h-8 px-2 border-l border-summit-hairline"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {loading && !data && (
        <div className="p-6 text-sm text-gray-500 border border-summit-hairline rounded-md bg-white" data-testid="schedule-loading">
          Loading schedule…
        </div>
      )}
      {error && (
        <div className="p-4 border border-rose-200 bg-rose-50 text-sm text-rose-800 rounded-md" data-testid="schedule-error">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="schedule-legend">
            {Object.entries(data.code_colors).map(([code, meta]) => (
              <span
                key={code}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-summit-hairline bg-white text-[11px] font-mono"
                data-testid={`schedule-legend-${code}`}
              >
                <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: meta.bg }} />
                <span className="text-gray-700">{code}</span>
                <span className="text-gray-400">{meta.label}</span>
              </span>
            ))}
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-summit-hairline bg-white text-[11px] font-mono"
              data-testid="schedule-legend-travel"
            >
              <span className="inline-flex items-center justify-center w-3 h-3 rounded-sm" style={{ backgroundColor: "#0369a1" }}>
                <Plane className="w-2 h-2 text-white" />
              </span>
              <span className="text-gray-700">Travel</span>
              {canEditTravel && <span className="text-gray-400">click a day to add</span>}
            </span>
          </div>

          <div className="border border-summit-hairline rounded-md bg-white overflow-auto" data-testid="schedule-grid">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-summit-primary text-white">
                  <th
                    className="sticky left-0 bg-summit-primary z-20 px-3 py-2 text-left font-mono text-[11px] uppercase tracking-widest border-r border-white/10"
                    style={{ minWidth: "220px" }}
                  >
                    Employee
                  </th>
                  {dayHeaders.map((h) => (
                    <th
                      key={h.iso}
                      className="px-1 py-1 text-center border-l border-white/10 min-w-[38px]"
                    >
                      <div className="font-mono text-[9px] uppercase opacity-70">{h.weekday}</div>
                      <div className="font-mono text-[13px] font-semibold">{h.dayOfMonth}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.locations.map((locGrp) => {
                  const folded = collapsedCities.has(locGrp.city);
                  return (
                    <React.Fragment key={locGrp.city}>
                      <tr
                        className="text-white cursor-pointer select-none"
                        onClick={() => toggleCity(locGrp.city)}
                        data-testid={`schedule-city-header-${locGrp.city}`}
                      >
                        <td
                          colSpan={1 + dayHeaders.length}
                          className="sticky left-0 z-10 px-3 py-2 font-mono text-[11px] uppercase tracking-widest"
                          style={{ backgroundColor: "#0f2947" /* deep navy */ }}
                        >
                          <span className="inline-flex items-center gap-2">
                            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${folded ? "rotate-0" : "rotate-90"}`} />
                            {locGrp.label}
                            <span className="text-white/50">
                              · {locGrp.user_count} employee{locGrp.user_count !== 1 ? "s" : ""}
                            </span>
                          </span>
                        </td>
                      </tr>
                      {!folded && locGrp.departments.map((dept) => (
                        <React.Fragment key={`${locGrp.city}|${dept.department}`}>
                          <tr data-testid={`schedule-dept-header-${locGrp.city}-${dept.department}`}>
                            <td
                              colSpan={1 + dayHeaders.length}
                              className="sticky left-0 z-10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-summit-ink bg-summit-glacier border-b border-summit-hairline"
                            >
                              <span className="pl-5">{dept.department} · {dept.user_count}</span>
                            </td>
                          </tr>
                          {dept.chains.map((ch) => (
                            <React.Fragment key={ch.lead.id}>
                              <UserRow
                                u={ch.lead}
                                isLead={ch.members.length > 0}
                                days={data.days}
                                colors={data.code_colors}
                                onDayClick={canEditTravel ? openTravelDialog : undefined}
                              />
                              {ch.members.map((m) => (
                                <UserRow key={m.id} u={m} indent days={data.days} colors={data.code_colors} onDayClick={canEditTravel ? openTravelDialog : undefined} />
                              ))}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Dialog open={!!travelDlg} onOpenChange={(o) => { if (!o) setTravelDlg(null); }}>
            <DialogContent className="sm:max-w-md" data-testid="travel-note-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Plane className="w-4 h-4 text-sky-700" />
                  Travel details
                </DialogTitle>
                <DialogDescription>
                  {travelDlg?.userName} · {travelDlg?.day}
                  {travelDlg?.existing && travelDlg?.setByName ? ` · set by ${travelDlg.setByName}` : ""}
                </DialogDescription>
              </DialogHeader>
              <Textarea
                value={travelDlg?.note || ""}
                onChange={(e) => setTravelDlg((p) => ({ ...p, note: e.target.value }))}
                placeholder="e.g. Flying YYC → YZF on WS 341, hotel: Explorer. Return Fri."
                rows={4}
                maxLength={500}
                data-testid="travel-note-input"
              />
              <DialogFooter className="gap-2 sm:justify-between">
                {travelDlg?.existing ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={removeTravel}
                    disabled={travelSaving}
                    className="text-rose-700 border-rose-200 hover:bg-rose-50"
                    data-testid="travel-note-remove-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" />
                    Remove
                  </Button>
                ) : <span />}
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" onClick={() => setTravelDlg(null)} disabled={travelSaving} data-testid="travel-note-cancel-btn">
                    Cancel
                  </Button>
                  <Button type="button" onClick={saveTravel} disabled={travelSaving} data-testid="travel-note-save-btn">
                    {travelSaving ? "Saving…" : travelDlg?.existing ? "Update" : "Save"}
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500" data-testid="schedule-footer">
            <CalendarDays className="w-3.5 h-3.5" />
            <span>
              Dates are projected from each user&apos;s shift schedule + time-off + stat holidays. Real hours are recorded on the Timesheet.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
